import {ReactElement, Ref, useEffect, useImperativeHandle, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {flex} from "@pg-design/helpers-css";
import {InfoIcon} from "@pg-design/icons";
import {Loader} from "@pg-design/loader";
import Highcharts, {chart, Options, Point, SeriesOptionsType} from "highcharts";
import annotationsModule from "highcharts/modules/annotations";
import exportData from "highcharts/modules/export-data";
import exportingModule from "highcharts/modules/exporting";
import mapModule from "highcharts/modules/map";
import offlineExportingModule from "highcharts/modules/offline-exporting";
import HighchartsReact from "highcharts-react-official";
import roundedCornersModule from "highcharts-rounded-corners";
import {escape, merge} from "lodash-es";

import {defaultOptions} from "../../../../config/chart_defaults";
import {slugifyChartTitle, stringifyJSXElement} from "../../../../utils/slugify_chart_title";
import {colorSets, ColorSetType, mapHoverColors} from "../../common/app/constants/colors";
import {RequestStatus} from "../../common/app/constants/enums";
import {breakpoints} from "../../common/app/styles/breakpoints";
import {ChartExportType} from "../card/components/ExportMenu";
import {rotate} from "../css_helpers/rotate";
import {ChartError} from "./ChartError";

if (typeof Highcharts === "object") {
    annotationsModule(Highcharts);
    exportingModule(Highcharts);
    offlineExportingModule(Highcharts);
    mapModule(Highcharts);
    exportData(Highcharts);
    roundedCornersModule(Highcharts);
}

export interface IChartProps {
    colorSet?: ColorSetType;
    customOptions?: Options;
    dataStatus: RequestStatus;
    height?: number | null;
    innerRef?: Ref<any>;
    onInit?: (chart: Highcharts.Chart) => void;
    series?: SeriesOptionsType[];
    type?: "chart" | "map";
    title?: ReactElement;
}

export interface ExtendedPoint extends Point {
    id: string;
}

export const Chart = (props: IChartProps) => {
    const {colorSet, customOptions, dataStatus, height, innerRef, onInit, series, type, title} = props;
    const {i18n} = useTranslation();
    const chartRef = useRef<any>(null);
    const [allowInteractions, setAllowInteractions] = useState<boolean>(false);

    // disable any interactions with the chart until it's loading animation is completed
    useEffect(() => {
        const timeout = setTimeout(() => setAllowInteractions(true), 1750);
        return () => clearTimeout(timeout);
    }, []);

    // prevents "hanging" the tooltip while scrolling the page with a scroll wheel
    useEffect(() => {
        const callback = () => chartRef?.current?.chart.tooltip.destroy();
        window.addEventListener("wheel", callback);
        return () => window.removeEventListener("wheel", callback);
    }, []);

    const theme = useTheme();

    const slugifiedChartTitle = title ? slugifyChartTitle(title) : "";
    const stringifiedChartTitle = title ? stringifyJSXElement(title) : "";

    useImperativeHandle(innerRef, () => ({
        exportChart: (type: ChartExportType) => {
            const source = window && typeof window !== "undefined" ? escape(window.location.href) : "";
            if (type === "print") {
                chartRef?.current?.chart.print();
            }
            if (["image/png", "image/jpeg", "application/pdf", "image/svg+xml"].includes(type)) {
                chartRef?.current?.chart.exportChartLocal(
                    {
                        filename: slugifiedChartTitle,
                        type
                    },
                    {
                        title: {
                            text: stringifiedChartTitle
                        },
                        subtitle: {
                            text: source
                        }
                    }
                );
            }
            if (type === "xls") {
                chartRef?.current?.chart.downloadXLS();
            }
        },
        hideTooltip: () => {
            chartRef?.current?.chart.tooltip.destroy();
        },
        highlightPoint: (pointIndex: number | null) => {
            const currentChart = chartRef?.current?.chart;

            if (pointIndex !== null) {
                currentChart.series[0].points[pointIndex].setState("hover");
                currentChart.tooltip.refresh(currentChart.series[0].points[pointIndex]);
            } else {
                currentChart.series[0].points.forEach((point: Point) => {
                    point.setState("normal");
                });
                currentChart.tooltip.hide();
            }
        }
    }));

    const handleCallback = (chart: Highcharts.Chart) => onInit && onInit(chart);

    const initialOptions: Options = {
        chart: {
            height,
            reflow: true
        },
        colors: colorSets[colorSet ?? "alternate"],
        plotOptions: {
            map: {
                states: {
                    hover: {
                        color: mapHoverColors[colorSet ?? "alternate"]
                    }
                }
            }
        },
        series
    };

    const options = merge({}, defaultOptions(title ?? <></>, i18n), initialOptions, customOptions);

    return (
        <StyledWrapper height={height}>
            {dataStatus === RequestStatus.PENDING && (
                <StyledStatus>
                    <Loader size="lg" />
                </StyledStatus>
            )}

            {dataStatus === RequestStatus.ERROR && (
                <StyledStatus>
                    <InfoIcon css={rotate()} fill={theme.colors.danger} size="6" />
                </StyledStatus>
            )}

            {dataStatus === RequestStatus.SUCCESS && series && series.length > 0 && (
                <ChartError>
                    <StyledClickCapturer allowInteractions={allowInteractions}>
                        <HighchartsReact
                            callback={(chart: Highcharts.Chart) => handleCallback(chart)}
                            constructorType={type === "map" ? "mapChart" : "chart"}
                            highcharts={Highcharts}
                            options={options}
                            ref={chartRef}
                        />
                    </StyledClickCapturer>
                </ChartError>
            )}
        </StyledWrapper>
    );
};

const StyledWrapper = styled.div<Pick<IChartProps, "height">>`
    height: ${(props) => (props.height ? props.height + "px" : "auto")};

    @media (min-width: ${breakpoints.md}) {
        width: 100%;
    }
`;

const StyledStatus = styled.div`
    width: 100%;
    height: 100%;
    ${flex("center", "center")};
    padding-bottom: 30px;
`;

const StyledClickCapturer = styled.div<{allowInteractions: boolean}>`
    pointer-events: ${({allowInteractions}) => (allowInteractions ? "inherit" : "none")};
`;
